import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  controlWebSocketUrl,
  createManualControlSession,
  createReconnectTicket,
  getManualOperatingTarget,
  releaseManualControlSession,
  sendManualAction,
  setManualOperatingMode,
} from '@/api/manualOperating'
import WhepPlayer from '@/components/manual-operating/WhepPlayer'
import { useAuth } from '@/auth/useAuth'
import type {
  ManualCommandResult,
  ManualControlSession,
  ManualControlParticipant,
  ManualOperatingAction,
  ManualOperatingTarget,
} from '@/types'
import { guardianAlphaConfigPath } from '@/utils/navigation'
import './manualOperating.css'

interface StoredSession {
  session_id: string
  reconnect_token: string
  requested_mode?: 'auto' | 'manual'
}

interface ActionGroup {
  key: string
  label: string
  actions: ManualOperatingAction[]
}

interface ManualActivity {
  actionLabel: string
  actor: ManualControlParticipant
  sentAt: string
}

function groupActions(actions: ManualOperatingAction[]): ActionGroup[] {
  return actions.reduce<ActionGroup[]>((groups, action) => {
    const existing = groups.find((group) => group.key === action.group)
    if (existing) {
      existing.actions.push(action)
      return groups
    }
    groups.push({ key: action.group, label: action.group_label, actions: [action] })
    return groups
  }, [])
}

export default function ManualOperatingDetailPage() {
  const { hasPermission } = useAuth()
  const canControl = hasPermission('guardian-alpha-restart:execute')
  const { targetId = '' } = useParams<{ targetId: string }>()
  const [target, setTarget] = useState<ManualOperatingTarget | null>(null)
  const [session, setSession] = useState<StoredSession | null>(null)
  const [mode, setMode] = useState<'auto' | 'manual'>('auto')
  const [socketStatus, setSocketStatus] = useState<'connecting' | 'connected' | 'grace' | 'closed'>('closed')
  const [lastPongAt, setLastPongAt] = useState<number | null>(null)
  const [result, setResult] = useState<ManualCommandResult | null>(null)
  const [lastActivity, setLastActivity] = useState<ManualActivity | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const socketRef = useRef<WebSocket | null>(null)
  const sessionRef = useRef<StoredSession | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const socketGenerationRef = useRef(0)
  const heartbeatTimersRef = useRef<Set<number>>(new Set())
  const explicitReleaseRef = useRef(false)
  const operationInFlightRef = useRef(false)

  const storageKey = `manual-operating:${targetId}`

  const connectControlSocket = useCallback(async (
    owned: StoredSession,
    initialTicket?: string,
    requireOpen = false,
  ) => {
    const generation = ++socketGenerationRef.current
    setSocketStatus('connecting')
    setLastPongAt(null)
    try {
      const ticket = initialTicket
        ?? (await createReconnectTicket(owned.session_id, owned.reconnect_token)).ticket
      const socket = new WebSocket(controlWebSocketUrl(owned.session_id, ticket))
      socketRef.current = socket
      let heartbeatTimer: number | null = null
      let opened = false
      const stopHeartbeat = () => {
        if (heartbeatTimer === null) return
        window.clearInterval(heartbeatTimer)
        heartbeatTimersRef.current.delete(heartbeatTimer)
        heartbeatTimer = null
      }
      const openedConnection = new Promise<void>((resolve, reject) => {
        socket.onopen = () => {
          if (socketGenerationRef.current !== generation || socketRef.current !== socket) return
          opened = true
          setSocketStatus('connected')
          setError(null)
          stopHeartbeat()
          heartbeatTimer = window.setInterval(() => {
            if (
              socketGenerationRef.current !== generation
              || socketRef.current !== socket
              || socket.readyState !== WebSocket.OPEN
            ) {
              stopHeartbeat()
              return
            }
            socket.send(JSON.stringify({ type: 'ping' }))
          }, 5000)
          heartbeatTimersRef.current.add(heartbeatTimer)
          resolve()
        }
        socket.onmessage = (event) => {
          if (socketGenerationRef.current !== generation || socketRef.current !== socket) return
          try {
            const message = JSON.parse(event.data) as {
              type?: string
              status?: string
              detail?: string
              requested_mode?: 'auto' | 'manual'
              edge_connected?: boolean
              participant_count?: number
              participants?: ManualControlParticipant[]
              action_label?: string
              actor?: ManualControlParticipant
              sent_at?: string
            }
            if (message.type === 'session_state') {
              if (message.requested_mode) {
                setMode(message.requested_mode)
                const updated = { ...owned, requested_mode: message.requested_mode }
                setSession(updated)
                sessionRef.current = updated
                sessionStorage.setItem(storageKey, JSON.stringify(updated))
              }
              if (typeof message.edge_connected === 'boolean') {
                const edgeConnected = message.edge_connected
                setTarget((current) => current
                  ? {
                      ...current,
                      edge_connected: edgeConnected,
                      requested_mode: message.requested_mode ?? current.requested_mode,
                      participant_count: message.participant_count ?? current.participant_count,
                      participants: message.participants ?? current.participants,
                      control_session_active: (message.participant_count ?? current.participant_count) > 0,
                    }
                  : current)
              }
            }
            if (
              message.type === 'action_sent'
              && message.action_label
              && message.actor
              && message.sent_at
            ) {
              setLastActivity({
                actionLabel: message.action_label,
                actor: message.actor,
                sentAt: message.sent_at,
              })
            }
            if (message.type === 'pong') {
              setLastPongAt(Date.now())
            }
            if (message.status === 'edge_offline' || message.status === 'failed') {
              setResult({ status: message.status, detail: message.detail })
            }
          } catch {
            // Presence messages are optional UI hints.
          }
        }
        socket.onerror = () => {
          if (requireOpen && !opened) {
            reject(new Error('제어 연결을 열지 못했습니다.'))
          }
        }
        socket.onclose = () => {
          stopHeartbeat()
          if (socketGenerationRef.current !== generation || socketRef.current !== socket) return
          if (requireOpen && !opened) {
            setSocketStatus('closed')
            reject(new Error('제어 연결이 열리기 전에 종료되었습니다.'))
            return
          }
          if (explicitReleaseRef.current) {
            setSocketStatus('closed')
            return
          }
          setSocketStatus('grace')
          reconnectTimerRef.current = window.setTimeout(() => {
            void connectControlSocket(owned)
          }, 1000)
        }
      })
      if (requireOpen) await openedConnection
    } catch (caught: unknown) {
      if (requireOpen) {
        setSocketStatus('closed')
        throw caught
      }
      const status = (caught as { status?: number }).status
      if (status === 404 || status === 409 || status === 410) {
        sessionStorage.removeItem(storageKey)
        setSession(null)
        sessionRef.current = null
        setMode('auto')
        setSocketStatus('closed')
        setError('제어 세션의 15초 재연결 유예가 끝났습니다. 필요하면 제어를 다시 시작하세요.')
        return
      }
      setSocketStatus('grace')
      setError((caught as { message?: string }).message ?? '제어 연결을 복구하지 못했습니다.')
      if (!explicitReleaseRef.current) {
        reconnectTimerRef.current = window.setTimeout(() => {
          void connectControlSocket(owned)
        }, 1000)
      }
    }
  }, [storageKey])

  useEffect(() => {
    if (!targetId) return
    explicitReleaseRef.current = false
    let cancelled = false
    async function load() {
      try {
        const loaded = await getManualOperatingTarget(targetId)
        if (cancelled) return
        setTarget(loaded)
        const storedRaw = sessionStorage.getItem(storageKey)
        if (storedRaw) {
          const stored = JSON.parse(storedRaw) as StoredSession
          setSession(stored)
          sessionRef.current = stored
          setMode(stored.requested_mode ?? 'auto')
          await connectControlSocket(stored)
          return
        }
      } catch (caught: unknown) {
        if (!cancelled) setError(
          (caught as { message?: string }).message ?? 'Manual Operating 화면을 불러오지 못했습니다.',
        )
      }
    }
    void load()
    const presenceTimer = window.setInterval(() => {
      void getManualOperatingTarget(targetId).then((loaded) => {
        if (!cancelled) setTarget(loaded)
      }).catch(() => {})
    }, 5000)
    function releaseOnSpaExit() {
      const owned = sessionRef.current
      if (owned && !explicitReleaseRef.current) {
        // React cleanup is the reliable signal for an in-app route change.
        // Hard page unload cannot be distinguished from reload, so it relies
        // on the control WebSocket's reconnect grace instead.
        explicitReleaseRef.current = true
        void releaseManualControlSession(
          owned.session_id,
          owned.reconnect_token,
          { keepalive: true },
        ).catch(() => {})
        sessionStorage.removeItem(storageKey)
        sessionRef.current = null
      }
      socketRef.current?.close()
    }
    return () => {
      cancelled = true
      socketGenerationRef.current += 1
      releaseOnSpaExit()
      explicitReleaseRef.current = true
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current)
      heartbeatTimersRef.current.forEach((timer) => window.clearInterval(timer))
      heartbeatTimersRef.current.clear()
      window.clearInterval(presenceTimer)
    }
  }, [connectControlSocket, storageKey, targetId])

  async function enableManual() {
    if (!target || operationInFlightRef.current) return
    operationInFlightRef.current = true
    setBusy(true)
    setError(null)
    let owned = session
    try {
      if (!owned) {
        const created: ManualControlSession = await createManualControlSession(target.target_id)
        owned = {
          session_id: created.session_id,
          reconnect_token: created.reconnect_token,
          requested_mode: created.requested_mode,
        }
        explicitReleaseRef.current = false
        sessionStorage.setItem(storageKey, JSON.stringify(owned))
        setSession(owned)
        sessionRef.current = owned
        setTarget((current) => current ? {
          ...current,
          requested_mode: created.requested_mode,
          participant_count: created.participant_count,
          participants: created.participants,
          control_session_active: created.participant_count > 0,
        } : current)
        await connectControlSocket(owned, created.ticket, true)
      } else if (socketStatus !== 'connected') {
        await connectControlSocket(owned, undefined, true)
      }

      const response = await setManualOperatingMode(target.target_id, 'manual', owned.session_id)
      setResult({
        ...response,
        detail: response.command_sent === false
          ? '기존 Manual 상태를 유지하며 공동 제어에 참여했습니다.'
          : response.detail,
      })
      if (response.status !== 'sent_unconfirmed') {
        throw new Error(response.detail ?? 'Manual 명령을 edge에 전송하지 못했습니다.')
      }
      setMode('manual')
      setTarget((current) => current ? {
        ...current,
        requested_mode: response.requested_mode ?? 'manual',
        participant_count: response.participant_count ?? current.participant_count,
        participants: response.participants ?? current.participants,
        control_session_active: (response.participant_count ?? current.participant_count) > 0,
      } : current)
      const updated = { ...owned, requested_mode: 'manual' as const }
      setSession(updated)
      sessionRef.current = updated
      sessionStorage.setItem(storageKey, JSON.stringify(updated))
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '공동 Manual 제어에 참여하지 못했습니다.')
      if (owned) {
        explicitReleaseRef.current = true
        await releaseManualControlSession(
          owned.session_id,
          owned.reconnect_token,
        ).catch(() => {})
        sessionStorage.removeItem(storageKey)
        socketRef.current?.close()
        setSession(null)
        sessionRef.current = null
        setMode('auto')
        explicitReleaseRef.current = false
      }
    } finally {
      operationInFlightRef.current = false
      setBusy(false)
    }
  }

  async function disableManual() {
    if (!session || operationInFlightRef.current) return
    operationInFlightRef.current = true
    setBusy(true)
    setError(null)
    explicitReleaseRef.current = true
    try {
      const released = await releaseManualControlSession(
        session.session_id,
        session.reconnect_token,
      )
      setResult({
        status: 'sent_unconfirmed',
        detail: released.participant_count > 0
          ? `공동 제어에서 나갔습니다. 활성 운영자 ${released.participant_count}명이 Manual을 유지합니다.`
          : '마지막 참여자로 공동 제어를 종료하고 Auto 복귀를 요청했습니다. 현장 실행은 확인되지 않습니다.',
      })
      setMode('auto')
      setTarget((current) => current ? {
        ...current,
        requested_mode: released.requested_mode,
        participant_count: released.participant_count,
        participants: released.participants,
        control_session_active: released.participant_count > 0,
      } : current)
      sessionStorage.removeItem(storageKey)
      socketRef.current?.close()
      setSession(null)
      sessionRef.current = null
    } catch (caught: unknown) {
      explicitReleaseRef.current = false
      setError(
        ((caught as { message?: string }).message ?? 'Auto 복귀 요청에 실패했습니다.')
        + ' 제어 연결을 유지하며 다시 시도할 수 있습니다.',
      )
    } finally {
      operationInFlightRef.current = false
      setBusy(false)
    }
  }

  async function sendAction(action: ManualOperatingAction) {
    if (!target || !session || mode !== 'manual' || operationInFlightRef.current) return
    operationInFlightRef.current = true
    setBusy(true)
    setError(null)
    try {
      const response = await sendManualAction(target.target_id, action.key, session.session_id)
      setResult(response)
      if (response.actor && response.sent_at) {
        setLastActivity({
          actionLabel: action.label,
          actor: response.actor,
          sentAt: response.sent_at,
        })
      }
    } catch (caught: unknown) {
      setError((caught as { message?: string }).message ?? '이벤트 명령 전송에 실패했습니다.')
    } finally {
      operationInFlightRef.current = false
      setBusy(false)
    }
  }

  const manualEnabled = mode === 'manual' && session !== null
  const groupManual = target?.requested_mode === 'manual'
  const actionGroups = groupActions(target?.actions ?? [])
  const switchDisabled = busy || !canControl
    || (!manualEnabled && (!target?.ready || !target.edge_connected))
  const actionDisabled = busy || !canControl || !manualEnabled
    || !target?.edge_connected || socketStatus !== 'connected'

  return (
    <div className="manual-operating-page">
      <nav className="manual-page-navigation" aria-label="Manual Operating navigation">
        <Link className="manual-back" to="/guardian-alpha/manual-operating">← 대상 목록</Link>
        {target && (
          <Link
            className="manual-config-link"
            to={guardianAlphaConfigPath(target.physical_node_id, target.target_id)}
          >
            GuardianAlpha Config 열기
          </Link>
        )}
      </nav>
      <h1 className="manual-heading">{target?.display_name ?? 'Manual Operating'}</h1>
      {target && <p className="manual-subtitle">
        명령 대상: {target.command_node_id ?? '확인 필요'} · 실행 결과 ACK 미지원
      </p>}
      <div className="manual-badges">
        <span className={`manual-badge ${target?.edge_connected ? 'online' : 'offline'}`}>
          {target?.edge_connected ? 'edge 연결됨' : 'edge offline'}
        </span>
        {session && (
          <span className={`manual-badge ${socketStatus === 'connected' ? 'online' : 'warning'}`}>
            제어 연결: {socketStatus}
          </span>
        )}
        {session && socketStatus === 'connected' && (
          <span className={`manual-badge ${lastPongAt ? 'online' : 'warning'}`}>
            {lastPongAt ? 'heartbeat 응답 확인' : 'heartbeat 응답 대기'}
          </span>
        )}
        {target && target.participant_count > 0 && (
          <span className="manual-badge warning">
            {target.requested_mode === 'manual' ? 'Manual ON' : '제어 준비 중'} · 공동 제어 {target.participant_count}명
          </span>
        )}
      </div>
      {error && <div className="manual-result error" role="alert">{error}</div>}
      {!canControl && (
        <div className="manual-result error">
          Manual/Auto와 이벤트 전송에는 GuardianAlpha Restart 실행 권한이 필요합니다.
        </div>
      )}
      {result && (
        <div className={`manual-result ${result.status === 'sent_unconfirmed' ? 'ok' : 'error'}`}>
          {result.status === 'sent_unconfirmed'
            ? result.detail ?? '전송됨 — 현장 실행 여부는 확인되지 않습니다.'
            : result.detail ?? 'edge가 offline이거나 전송에 실패했습니다.'}
        </div>
      )}
      <div className="manual-camera-grid">
        {target?.cameras.map((camera) => (
          <WhepPlayer key={camera.name} cameraName={camera.name} whepUrl={camera.whep_url} />
        ))}
      </div>
      <div className="manual-command-layout">
        <section className="manual-card manual-control-console">
          <div className="manual-control-copy">
            <div className="manual-control-eyebrow">운전 모드</div>
            <div className="manual-control-title">수동 제어</div>
            <div className="manual-control-description">
              {manualEnabled
                ? '공동 Manual 제어에 참여 중입니다. 명령 버튼을 사용할 수 있습니다.'
                : groupManual
                  ? '다른 운영자가 Manual 운전 중입니다. 필요하면 공동 제어에 참여하세요.'
                  : '현재 Auto 운전 중입니다. 영상을 확인한 뒤 필요할 때만 켜세요.'}
            </div>
          </div>
          {target && target.participants.length > 0 && (
            <div className="manual-participants" aria-label="공동 제어 참여자">
              {target.participants.map((participant) => (
                <span className="manual-participant" key={participant.username}>
                  {participant.is_me ? '나' : participant.display_name}
                </span>
              ))}
            </div>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={manualEnabled}
            aria-label="Manual 모드"
            className={`manual-mode-switch ${manualEnabled ? 'on' : 'off'}`}
            disabled={switchDisabled}
            onClick={() => void (manualEnabled ? disableManual() : enableManual())}
          >
            <span className="manual-mode-switch-label">
              {busy
                ? '전환 중'
                : manualEnabled
                  ? 'Manual ON · 나가기'
                  : groupManual
                    ? 'Manual 참여'
                    : 'Auto · OFF'}
            </span>
            <span className="manual-mode-switch-track" aria-hidden="true">
              <span className="manual-mode-switch-thumb" />
            </span>
          </button>
          <div className="manual-control-safety-note">
            다른 화면으로 이동하면 나만 공동 제어에서 나갑니다. 마지막 참여자가 나가면 Auto로
            복귀하며, 비정상 종료·네트워크 단절은 15초 재연결 유예 후 같은 규칙을 적용합니다.
          </div>
          {target && !target.ready && (
            <div className="manual-control-blocked">
              준비되지 않음: {target.not_ready_reasons.join(', ')}
            </div>
          )}
        </section>
        <section className="manual-card manual-action-panel">
          <div className="manual-action-panel-heading">
            <div className="manual-control-title">현장 명령</div>
            <span className="manual-action-state">
              {manualEnabled ? '명령 가능' : 'Manual 모드를 켜면 활성화됩니다'}
            </span>
          </div>
          {lastActivity && (
            <div className="manual-action-activity" aria-live="polite">
              최근 전송 · {lastActivity.actor.is_me ? '나' : lastActivity.actor.display_name}
              {' · '}{lastActivity.actionLabel}
              {' · '}{new Date(lastActivity.sentAt).toLocaleTimeString()}
            </div>
          )}
          {actionGroups.length === 0 ? (
            <div className="manual-action-empty">
              등록된 현장 명령이 없습니다. GuardianAlpha Config의 Settings에서 프로필을 선택하세요.
            </div>
          ) : (
            <div className="manual-action-groups">
              {actionGroups.map((group) => (
                <section className="manual-action-group" key={group.key}>
                  {group.label && <h2 className="manual-action-group-title">{group.label}</h2>}
                  <div className="manual-action-grid">
                    {group.actions.map((action) => (
                      <button
                        key={action.key}
                        className={`manual-action-button ${action.tone}`}
                        disabled={actionDisabled || (action.requires_manual && !manualEnabled)}
                        onClick={() => void sendAction(action)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
